import React, { useRef, useEffect, useState, useImperativeHandle, useCallback } from 'react';
import { fabric } from 'fabric';
import { v4 as uuidv4 } from 'uuid';
import { black_icons, icons } from '../../icons';
import { toBase64 } from '../../Utils/canvasUtils';

if (fabric && fabric.Text) {
  fabric.Text.prototype._setTextStyles = function (ctx, charStyle, forMeasuring) {
    ctx.textBaseline = 'alphabetic';
    if (this.path) {
      switch (this.pathAlign) {
        case 'center':
          ctx.textBaseline = 'middle';
          break;
        case 'ascender':
          ctx.textBaseline = 'top';
          break;
        case 'descender':
          ctx.textBaseline = 'bottom';
          break;
      }
    }
    ctx.font = this._getFontDeclaration(charStyle, forMeasuring);
  };
}

const getWrappedPosition = (pos, max) => {
  return ((pos % max) + max) % max;
};

function Canvas({ svgContent, svgDimensions,
  selectedTool, setSelectedTool, selectedColor, selectedNomexColor,
  canvasState, setCanvasState, setEnvelopeDimension,
  defaultColor, defaultNomexColor, history, setHistory,
  redoStack, setRedoStack, viewMode, paraventSize, setHighLightColor, style }, ref) {
  const canvasContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasStateRef = useRef(canvasState);
  const [canvas, setCanvas] = useState(null);

  useEffect(() => {
    canvasStateRef.current = canvasState;
  }, [canvasState]);

  const historyLimit = 100;

  const [iEnvelopeGrid, setIEnvelopeGrid] = useState([]);
  const iEnvelopeGridRef = useRef([]);
  useEffect(() => {
    iEnvelopeGridRef.current = iEnvelopeGrid;
  }, [iEnvelopeGrid]);
  const [iParaventGrid, setIParaventGrid] = useState(null);
  const [iImageGroup, setIImageGroup] = useState([]);
  const [iTextGroup, setITextGroup] = useState([]);
  const [iLabel, setILabel] = useState(null);
  const [iParaventLogo, setIParaventLogo] = useState(null);
  const [envelopeClipPath, setEnvelopeClipPath] = useState(null);
  const [arrowY, setArrowY] = useState(0);
  const [showArrows, setShowArrows] = useState(false);
  const importantParameters = ['id', 'type', 'src', 'fileName', 'text', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fill', 'left', 'top', 'angle', 'scaleX', 'scaleY', 'centerPointX', 'lineHeight', 'pairId', 'originX', 'originY', 'opacity', 'isGhost', 'width', 'height', 'isBanner']
  const viewModeRef = useRef(viewMode);
  useEffect(() => { viewModeRef.current = viewMode; }, [viewMode]);






  const getAllImportantInformations = (object) => {
    if (!object) return {};
    const objectRepr = {}
    importantParameters.forEach(param => {
      // Initialize with default values to prevent V8 deoptimization (shape changes)
      let defaultValue = null;
      if (['angle', 'scaleX', 'scaleY', 'left', 'top', 'centerPointX', 'centerPointY', 'lineHeight', 'opacity'].includes(param)) {
        defaultValue = 1; // scale/opacity defaults to 1
        if (['angle', 'left', 'top', 'centerPointX', 'centerPointY'].includes(param)) defaultValue = 0;
      }

      objectRepr[param] = object[param] !== undefined ? object[param] : defaultValue;
    })
    if (object.type !== "i-text" && object.type !== "text") {
      objectRepr['src'] = object.src || (object.getSrc && object.getSrc());
    }
    objectRepr['centerPointX'] = (object.getCenterPoint && object.getCenterPoint()) ? object.getCenterPoint().x : (object.centerPointX || 0);
    objectRepr['centerPointY'] = (object.getCenterPoint && object.getCenterPoint()) ? object.getCenterPoint().y : (object.centerPointY || 0);
    return objectRepr;
  }

  const compareObjects = (object1, object2) => {
    const o = { id: object1.id };
    const n = { id: object2.id };
    importantParameters.forEach(param => {
      if (object1[param] !== object2[param]) {
        o[param] = object1[param]
        n[param] = object2[param]
      }
    })
    return { old: o, new: n }
  }

  const updateCanvasState = (objects, historyRecord = true) => {
    const objectList = Array.isArray(objects) ? objects : [objects];
    const historyEntries = [];

    const currentState = canvasStateRef.current;
    if (!currentState) return;

    // Surgical copy instead of deep clone
    let updatedState = {
      ...currentState,
      images: [...(currentState.images || [])],
      texts: [...(currentState.texts || [])]
    };

    objectList.forEach(object => {
      let historyEntry = {};

      if (Object.keys(object).length === 2 && object.hasOwnProperty('id') && object.hasOwnProperty('type')) {
        const isImage = object.type !== 'i-text' && object.type !== 'text';
        historyEntry.path = isImage ?
          (object.id === "paravent-logo" ? "paraventLogo" : "images") :
          "texts";

        if (isImage) {
          if (object.id === "paravent-logo") {
            historyEntry.old = currentState.paraventLogo;
            updatedState.paraventLogo = null;
          } else {
            const existingImageIndex = updatedState.images.findIndex(element => element.id === object.id);
            if (existingImageIndex !== -1) {
              historyEntry.old = updatedState.images[existingImageIndex];
              updatedState.images.splice(existingImageIndex, 1);
            }
          }
        } else {
          const existingTextIndex = updatedState.texts.findIndex(element => element.id === object.id);
          if (existingTextIndex !== -1) {
            historyEntry.old = updatedState.texts[existingTextIndex];
            updatedState.texts.splice(existingTextIndex, 1);
          }
        }
        historyEntry.new = null;
      } else {
        const isImage = object.type !== 'i-text' && object.type !== 'text';
        if (isImage) {
          if (object.id === "paravent-logo") {
            historyEntry.path = "paraventLogo";
            if (currentState.paraventLogo) {
              historyEntry = { ...historyEntry, ...compareObjects(currentState.paraventLogo, object) };
            } else {
              historyEntry.id = object.id;
              historyEntry.old = null;
              historyEntry.new = object;
            }
            updatedState.paraventLogo = object;
          } else {
            const existingImageIndex = updatedState.images.findIndex(element => element.id === object.id);
            if (existingImageIndex !== -1) {
              historyEntry.path = "images";
              historyEntry = { ...historyEntry, ...compareObjects(updatedState.images[existingImageIndex], object) };
              updatedState.images[existingImageIndex] = object;
            } else {
              historyEntry.path = "images";
              historyEntry.id = object.id;
              historyEntry.old = null;
              historyEntry.new = object;
              updatedState.images.push(object);
            }
          }
        } else {
          const existingTextIndex = updatedState.texts.findIndex(element => element.id === object.id);
          if (existingTextIndex !== -1) {
            historyEntry.path = "texts";
            historyEntry = { ...historyEntry, ...compareObjects(updatedState.texts[existingTextIndex], object) };
            updatedState.texts[existingTextIndex] = object;
          } else {
            historyEntry.path = "texts";
            historyEntry.id = object.id;
            historyEntry.old = null;
            historyEntry.new = object;
            updatedState.texts.push(object);
          }
        }
      }
      if (Object.keys(historyEntry).length > 0) {
        historyEntries.push(historyEntry);
      }
    });

    if (canvas) {
      const canvasObjects = canvas.getObjects();
      const objectIndexMap = new Map();
      canvasObjects.forEach((obj, index) => {
        if (obj.id) objectIndexMap.set(obj.id, index);
      });

      const deduplicate = (list) => {
        const unique = [];
        const ids = new Set();
        list.forEach(item => {
          if (item && item.id && !ids.has(item.id)) {
            unique.push(item);
            ids.add(item.id);
          }
        });
        return unique;
      };

      updatedState.images = deduplicate(updatedState.images);
      updatedState.texts = deduplicate(updatedState.texts);
      updatedState.images.sort((a, b) => (objectIndexMap.get(a.id) ?? 0) - (objectIndexMap.get(b.id) ?? 0))
      updatedState.texts.sort((a, b) => (objectIndexMap.get(a.id) ?? 0) - (objectIndexMap.get(b.id) ?? 0))
    }

    canvasStateRef.current = updatedState;
    setCanvasState(updatedState);

    if (historyRecord && historyEntries.length > 0) {
      setHistory((prevHistory) => {
        const newHistory = [...prevHistory, historyEntries];
        return newHistory.slice(newHistory.length - historyLimit);
      });
      setRedoStack([]);
    }
  };

  const commitCanvasState = (objects, history = true) => {
    updateCanvasState(objects, history);
  };

  const addImage = (imageData, paravent, history = true, stateUpdate = true, clipPath = null) => {
    return new Promise((resolve) => {
      if (canvas) {
        const src = imageData.src;
        const isSvg = typeof src === 'string' && (src.toLowerCase().startsWith('data:image/svg') || src.toLowerCase().endsWith('.svg'));
        if (src && isSvg) {
          fabric.loadSVGFromURL(src, (objects, options) => {
            const img = fabric.util.groupSVGElements(objects, options);

            let scaleX = 1;
            let scaleY = 1;

            if (!clipPath) {
              if (paravent && iParaventGrid) clipPath = iParaventGrid;
              else if (!paravent && envelopeClipPath) clipPath = envelopeClipPath;
            }

            if (img.width > svgDimensions.width) {
              if (paravent) {
                scaleX = (clipPath ? clipPath.radius : svgDimensions.width / 4) / img.width;
              } else {
                scaleX = (clipPath ? clipPath.width : svgDimensions.width) / img.width;
              }
            }
            if (img.height > svgDimensions.height) {
              if (paravent) {
                scaleY = (clipPath ? clipPath.radius : svgDimensions.height / 4) / img.height;
              } else {
                scaleY = (clipPath ? clipPath.height : svgDimensions.height) / img.height;
              }
            }

            const scale = Math.min(scaleX, scaleY);

            const clipPathCenterX = clipPath
              ? (paravent ? clipPath.left + clipPath.radius : clipPath.left + clipPath.width / 2)
              : svgDimensions.width / 2;

            const clipPathCenterY = clipPath
              ? (paravent ? clipPath.top + clipPath.radius : clipPath.top + clipPath.height / 2)
              : svgDimensions.height / 2;

            img.set({
              id: paravent ? "paravent-logo" : (imageData.id || uuidv4()),
              left: typeof imageData.left === 'number' ? imageData.left : clipPathCenterX - (img.width * scale) / 2,
              top: typeof imageData.top === 'number' ? imageData.top : clipPathCenterY - (img.height * scale) / 2,
              angle: imageData.angle || 0,
              scaleX: imageData.scaleX || scale,
              scaleY: imageData.scaleY || scale,
              selectable: ((selectedTool === "Graphic" && !paravent) || (selectedTool === "Paravent" && paravent)) && !imageData.isGhost,
              evented: ((selectedTool === "Graphic" && !paravent) || (selectedTool === "Paravent" && paravent)) && !imageData.isGhost,
              hoverCursor: "grab",
              visible: (selectedTool === "Graphic" || selectedTool === "Text") && !paravent || (selectedTool === "Paravent" && paravent),
              src: src,
              fileName: imageData.fileName,
              isBanner: imageData.isBanner
            });

            if (imageData.isBanner) {
              img.set({
                strokeUniform: true
              });
              if (img.getObjects) {
                img.getObjects().forEach(subObj => {
                  subObj.set({ strokeUniform: true });
                });
              }
            }

            if (imageData.width) {
              img.set('width', imageData.width);
            }
            if (imageData.height) {
              img.set('height', imageData.height);
            }

            paravent ?
              setIParaventLogo((prevParaventLogo) => { canvas.remove(prevParaventLogo); return img })
              : setIImageGroup((prevImageGroup) => [...prevImageGroup, img]);

            img.clipPath = clipPath;
            canvas.add(img);
            if (imageData.isBanner && !imageData.isGhost) {
              snapBannerToGrid(img);
            }
            if (stateUpdate) {
              const masterInfo = getAllImportantInformations(img);
              createGhost(img, true).then(ghostResult => {
                const batch = [masterInfo];
                if (ghostResult.added) batch.push(ghostResult.added);
                if (ghostResult.removed) batch.push(ghostResult.removed);
                updateCanvasState(batch, history);
              });
            }
            resolve(img);
          });
        } else {

          fabric.Image.fromURL(src, (img) => {
            let scaleX = 1;
            let scaleY = 1;

            if (!clipPath) {
              if (paravent && iParaventGrid) clipPath = iParaventGrid;
              else if (!paravent && envelopeClipPath) clipPath = envelopeClipPath;
            }

            if (img.width > svgDimensions.width) {
              if (paravent) {
                scaleX = (clipPath ? clipPath.radius : svgDimensions.width / 4) / img.width;
              } else {
                scaleX = (clipPath ? clipPath.width : svgDimensions.width) / img.width;
              }
            }
            if (img.height > svgDimensions.height) {
              if (paravent) {
                scaleY = (clipPath ? clipPath.radius : svgDimensions.height / 4) / img.height;
              } else {
                scaleY = (clipPath ? clipPath.height : svgDimensions.height) / img.height;
              }
            }

            const scale = Math.min(scaleX, scaleY);

            const clipPathCenterX = clipPath
              ? (paravent ? clipPath.left + clipPath.radius : clipPath.left + clipPath.width / 2)
              : svgDimensions.width / 2;

            const clipPathCenterY = clipPath
              ? (paravent ? clipPath.top + clipPath.radius : clipPath.top + clipPath.height / 2)
              : svgDimensions.height / 2;

             img.set({
              id: paravent ? "paravent-logo" : (imageData.id || uuidv4()),
              left: typeof imageData.left === 'number' ? imageData.left : clipPathCenterX - (img.width * scale) / 2,
              top: typeof imageData.top === 'number' ? imageData.top : clipPathCenterY - (img.height * scale) / 2,
              angle: imageData.angle || 0,
              scaleX: imageData.scaleX || scale,
              scaleY: imageData.scaleY || scale,
              selectable: ((selectedTool === "Graphic" && !paravent) || (selectedTool === "Paravent" && paravent)) && !imageData.isGhost,
              evented: ((selectedTool === "Graphic" && !paravent) || (selectedTool === "Paravent" && paravent)) && !imageData.isGhost,
              hoverCursor: "grab",
              visible: (selectedTool === "Graphic" || selectedTool === "Text") && !paravent || (selectedTool === "Paravent" && paravent),
              src: imageData.src,
              fileName: imageData.fileName,
              isBanner: imageData.isBanner
            })

            if (imageData.isBanner) {
              img.set({
                stroke: '#000000',
                strokeWidth: 1,
                strokeDashArray: [6, 3],
                strokeUniform: true
              });
            }

            if (imageData.width) {
              img.set('width', imageData.width);
            }
            if (imageData.height) {
              img.set('height', imageData.height);
            }
            paravent ?
              setIParaventLogo((prevParaventLogo) => { canvas.remove(prevParaventLogo); return img })
              : setIImageGroup((prevImageGroup) => [...prevImageGroup, img]);
            img.clipPath = clipPath;
            canvas.add(img);
            if (imageData.isBanner && !imageData.isGhost) {
              snapBannerToGrid(img);
            }
            if (stateUpdate) {
              const masterInfo = getAllImportantInformations(img);
              createGhost(img, true).then(ghostResult => {
                const batch = [masterInfo];
                if (ghostResult.added) batch.push(ghostResult.added);
                if (ghostResult.removed) batch.push(ghostResult.removed);
                updateCanvasState(batch, history);
              });
            }
            resolve(img);
          })
        }
      } else { resolve(null); }
    });
  }

  const addText = (textData, history = true, stateUpdate = true) => {
    return new Promise((resolve) => {
      if (canvas) {
        const canvasCenterX = svgDimensions.width / 2;
        const canvasCenterY = svgDimensions.height / 2;

        const newText = new fabric.IText(textData.text || "IT’S TIME TO FLY", {
          id: textData.id || uuidv4(),
          fontFamily: textData.fontFamily,
          fontWeight: textData.fontWeight,
          fontStyle: textData.fontStyle,
          fill: textData.fill,
          selectable: selectedTool === "Text" && !textData.isGhost,
          evented: selectedTool === "Text" && !textData.isGhost,
          visible: selectedTool === "Graphic" || selectedTool === "Text",
          angle: textData.angle || 0,
          scaleX: textData.scaleX || 1,
          scaleY: textData.scaleY || 1,
          top: textData.top || 0,
          left: textData.left || 0,
          padding: 0,
          lineHeight: textData.lineHeight || 1,
        })
        newText.set('left', typeof textData.left === 'number' ? textData.left : canvasCenterX - newText.width / 2);
        newText.set('top', typeof textData.top === 'number' ? textData.top : canvasCenterY - newText.height / 2);

        newText.clipPath = envelopeClipPath;
        canvas.add(newText)
        if (stateUpdate) {
          const masterInfo = getAllImportantInformations(newText);
          createGhost(newText, true).then(ghostResult => {
            const batch = [masterInfo];
            if (ghostResult.added) batch.push(ghostResult.added);
            if (ghostResult.removed) batch.push(ghostResult.removed);
            updateCanvasState(batch, history);
          });
        }
        setITextGroup((prevTextGroup) => [...prevTextGroup, newText]);
        resolve(newText);
      } else { resolve(null); }
    });
  }

  const modifyObject = (object, changes, history = true) => {
    object.set(changes);
    object.dirty = true;
    updateCanvasState(getAllImportantInformations(object), history)
    canvas.requestRenderAll();
  }

  const removeObject = (object, history = true) => {
    if (canvas && object) {
      const params = getAllImportantInformations(object)
      const isImage = object.type !== 'i-text' && object.type !== 'text';
      if (isImage || object.type === 'i-text') {
        const batch = [{ id: params.id, type: params.type }];

        // Handle ghost removal in a batch
        if (object.pairId && !object.isGhost) {
          const ghost = canvas.getObjects().find(o => o.id === object.pairId);
          if (ghost) {
            batch.push({ id: ghost.id, type: ghost.type });
             // Clean up ghost state collections
             const isGhostImage = ghost.type !== 'i-text' && ghost.type !== 'text';
             if (isGhostImage) {
               setIImageGroup((prev) => prev.filter(img => img.id !== ghost.id));
             } else {
               setITextGroup((prev) => prev.filter(txt => txt.id !== ghost.id));
             }
             canvas.remove(ghost);
          }
        }

        if (isImage) {
          if (object.id === "paravent-logo") {
            setIParaventLogo(null)
          } else {
            setIImageGroup((prevImageGroup) => prevImageGroup.filter(img => img.id !== object.id));
          }
        }
        else {
          setITextGroup((prevTextGroup) => prevTextGroup.filter(txt => txt.id !== object.id));
        }

        updateCanvasState(batch, history);
        canvas.remove(object);
      }
    }
  }

  const applyChangesToCanvas = (changes, action) => {
    changes.forEach((change) => {
      const id = change.id || (action === "undo" ? (change.new?.id || change.old?.id) : (change.old?.id || change.new?.id));

      if (change.path === "envelopeGrid") {
        iEnvelopeGrid[change.row][change.col].getObjects()[0].set('fill', action === "undo" ? change.old : change.new)
        iEnvelopeGrid[change.row][change.col].getObjects()[0].dirty = true;
      }
      if (change.path === "paraventLogo") {
        const target = iParaventLogo;
        if (action === "undo") {
          if (change.new && !change.old) removeObject(target, false);
          else if (!change.new && change.old) addImage(change.old, true, false);
          else if (target) modifyObject(target, change.old, false);
        } else {
          if (change.old && !change.new) removeObject(target, false);
          else if (!change.old && change.new) addImage(change.new, true, false);
          else if (target) modifyObject(target, change.new, false);
        }
      }
      if (change.path === "images") {
        const target = iImageGroup.find(image => image.id === id);
        if (action === "undo") {
          if (change.new && !change.old) removeObject(target, false);
          else if (!change.new && change.old) addImage(change.old, false, false);
          else if (target) modifyObject(target, change.old, false);
        } else {
          if (change.old && !change.new) removeObject(target, false);
          else if (!change.old && change.new) addImage(change.new, false, false);
          else if (target) modifyObject(target, change.new, false);
        }
      }
      if (change.path === "texts") {
        const target = iTextGroup.find(text => text.id === id);
        if (action === "undo") {
          if (change.new && !change.old) removeObject(target, false);
          else if (!change.new && change.old) addText(change.old, false, false);
          else if (target) modifyObject(target, change.old, false);
        } else {
          if (change.old && !change.new) removeObject(target, false);
          else if (!change.old && change.new) addText(change.new, false, false);
          else if (target) modifyObject(target, change.new, false);
        }
      }

    })
    setCanvasState((prevCanvasState) => {
      const nextState = { ...prevCanvasState };
      changes.forEach((change) => {
        if (change.path === "envelopeGrid") {
          nextState.envelopeGrid[change.row][change.col] = action === "undo" ? change.old : change.new;
        } else if (change.path === "paraventLogo") {
          nextState.paraventLogo = action === "undo"
            ? (change.old && change.new ? { ...nextState.paraventLogo, ...change.old } : change.old)
            : (change.old && change.new ? { ...nextState.paraventLogo, ...change.new } : change.new);
        } else if (change.path === "images") {
          const id = change.id || (action === "undo" ? (change.new ? change.new.id : change.old.id) : (change.old ? change.old.id : change.new.id));
          const idx = nextState.images.findIndex(img => img.id === id);
          if (action === "undo") {
            if (change.new && !change.old) nextState.images = nextState.images.filter(img => img.id !== id);
            else if (!change.new && change.old) nextState.images = [...nextState.images, change.old];
            else if (idx !== -1) nextState.images[idx] = { ...nextState.images[idx], ...change.old };
          } else {
            if (change.old && !change.new) nextState.images = nextState.images.filter(img => img.id !== id);
            else if (!change.old && change.new) nextState.images = [...nextState.images, change.new];
            else if (idx !== -1) nextState.images[idx] = { ...nextState.images[idx], ...change.new };
          }
        } else if (change.path === "texts") {
          const id = change.id || (action === "undo" ? (change.new ? change.new.id : change.old.id) : (change.old ? change.old.id : change.new.id));
          const idx = nextState.texts.findIndex(txt => txt.id === id);
          if (action === "undo") {
            if (change.new && !change.old) nextState.texts = nextState.texts.filter(txt => txt.id !== id);
            else if (!change.new && change.old) nextState.texts = [...nextState.texts, change.old];
            else if (idx !== -1) nextState.texts[idx] = { ...nextState.texts[idx], ...change.old };
          } else {
            if (change.old && !change.new) nextState.texts = nextState.texts.filter(txt => txt.id !== id);
            else if (!change.old && change.new) nextState.texts = [...nextState.texts, change.new];
            else if (idx !== -1) nextState.texts[idx] = { ...nextState.texts[idx], ...change.new };
          }
        } else if (change.path === "nomexBottomRows") {
          nextState.nomexBottomRows = action === "undo" ? change.old : change.new;
        }
      });
      canvasStateRef.current = nextState;
      return nextState;
    })
    canvas.requestRenderAll()
  }

  const snapBannerToGrid = (obj) => {
    const grid = iEnvelopeGridRef.current;
    if (!grid || grid.length === 0 || !canvas) return;

    const xLines = [];
    grid.forEach(row => {
      if (row) {
        row.forEach(panel => {
          if (panel) {
            xLines.push(panel.left);
            xLines.push(panel.left + panel.width);
          }
        });
      }
    });

    const uniqueX = Array.from(new Set(xLines.map(x => Math.round(x * 10) / 10))).sort((a, b) => a - b);

    if (uniqueX.length === 0) return;

    const canvasWidth = parseFloat(svgDimensions.width);

    // Support periodic canvas boundaries for wrapping without choppiness
    const extendedX = [
      ...uniqueX.map(x => x - canvasWidth),
      ...uniqueX,
      ...uniqueX.map(x => x + canvasWidth)
    ];

    const left = obj.left;
    const right = obj.left + obj.getScaledWidth();

    const nearestLeft = extendedX.reduce((prev, curr) => Math.abs(curr - left) < Math.abs(prev - left) ? curr : prev);
    const nearestRight = extendedX.reduce((prev, curr) => Math.abs(curr - right) < Math.abs(prev - right) ? curr : prev);

    const newWidth = Math.max(10, nearestRight - nearestLeft);

    obj.set({
      left: nearestLeft,
      scaleX: newWidth / obj.width,
      angle: 0
    });
    obj.setCoords();

    // Immediately keep the ghost completely synchronized to eliminate visual jumping
    if (obj.pairId) {
      const ghost = canvas.getObjects().find(o => o.id === obj.pairId);
      if (ghost) {
        syncGhost(obj, ghost);
      }
    }

    canvas.renderAll();
  };

  const permanentGhostRef = useRef(null);
  const temporaryGhostRef = useRef(null);
  const isCreatingTempGhostRef = useRef(false);

  const syncGhost = useCallback((master, ghost) => {
    if (!canvas) return;
    const canvasWidth = parseFloat(svgDimensions.width);
    const newLeft = master.left < canvasWidth / 2 ?
      master.left + canvasWidth :
      master.left - canvasWidth;

    ghost.set({
      left: newLeft,
      top: master.top,
      angle: master.angle,
      scaleX: master.scaleX,
      scaleY: master.scaleY,
      skewX: master.skewX,
      skewY: master.skewY,
      flipX: master.flipX,
      flipY: master.flipY,
      opacity: master.opacity ?? 1,
      originX: master.originX,
      originY: master.originY,
      clipPath: master.clipPath,
      dirty: true,
    });
    ghost.setCoords();
    canvas.renderAll();
  }, [canvas, svgDimensions.width]);

  const createGhost = useCallback(async (master, batchMode = false) => {
    if (!canvas) return null;
    const canvasWidth = parseFloat(svgDimensions.width);
    const objWidth = master.getScaledWidth();

    // Remove existing ghost if any
    let removedGhostInfo = null;
    if (master.pairId) {
      const oldGhost = canvas.getObjects().find(o => o.id === master.pairId);
      if (oldGhost) {
        const isOldGhostImage = oldGhost.type !== 'i-text' && oldGhost.type !== 'text';
        if (isOldGhostImage) {
          setIImageGroup((prev) => prev.filter(img => img.id !== oldGhost.id));
        } else {
          setITextGroup((prev) => prev.filter(txt => txt.id !== oldGhost.id));
        }
        removedGhostInfo = { id: oldGhost.id, type: oldGhost.type };
        if (!batchMode) {
          updateCanvasState(removedGhostInfo, false); // Remove from canvasState but don't add to history
        }
        canvas.remove(oldGhost);
      }
      master.pairId = null;
    }

    let ghostParams = null;
    if (master.left + objWidth > canvasWidth) {
      ghostParams = getAllImportantInformations(master);
      ghostParams.left = master.left - canvasWidth;
    } else if (master.left < 0) {
      ghostParams = getAllImportantInformations(master);
      ghostParams.left = master.left + canvasWidth;
    }

    if (ghostParams) {
      const newId = uuidv4();
      ghostParams.id = newId;
      ghostParams.isGhost = true;
      master.pairId = newId;

      let newlyAddedGhost;
      if (master.type !== 'i-text' && master.type !== 'text') {
        newlyAddedGhost = await addImage(ghostParams, false, false, false, master.clipPath);
      } else {
        newlyAddedGhost = await addText(ghostParams, false, false);
        if (newlyAddedGhost) newlyAddedGhost.clipPath = master.clipPath;
      }

      if (newlyAddedGhost) {
        newlyAddedGhost.pairId = master.id;
        newlyAddedGhost.set({
          selectable: false,
          evented: false,
          hasControls: false,
          isGhost: true
        });
        syncGhost(master, newlyAddedGhost);

        const addedGhostInfo = getAllImportantInformations(newlyAddedGhost);
        if (!batchMode) {
          updateCanvasState(addedGhostInfo, false);
        }

        return { added: addedGhostInfo, removed: removedGhostInfo };
      }
    }
    return { added: null, removed: removedGhostInfo };
  }, [canvas, svgDimensions.width, addImage, addText, syncGhost, getAllImportantInformations]);

  const handleTransform = useCallback((e) => {
    if (!canvas) return;
    const master = e.target;
    if (!master || !master.pairId) return;

    let ghost = permanentGhostRef.current;
    if (!ghost || ghost.id !== master.pairId) {
      ghost = canvas.getObjects().find(o => o.id === master.pairId);
      if (ghost) permanentGhostRef.current = ghost;
    }

    if (ghost) {
      syncGhost(master, ghost);
    }
  }, [canvas, syncGhost]);

  const enforceLayering = useCallback(() => {
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();

    canvas.getObjects('i-text').forEach(text => {
      text.bringToFront();
    });
    if (activeObject && activeObject.type !== 'i-text' && activeObject.type !== 'text') {
      activeObject.bringToFront();
    }

    canvas.requestRenderAll();
  }, [canvas]);

  useImperativeHandle(ref, () => ({
    addImage: (imageDataOrUrl) => {
      const data = typeof imageDataOrUrl === 'string' ? { src: imageDataOrUrl } : imageDataOrUrl;
      addImage(data, false);
    },
    addLogo: (imageDataOrUrl) => {
      const data = typeof imageDataOrUrl === 'string' ? { src: imageDataOrUrl } : imageDataOrUrl;
      addImage(data, true);
    },
    addBanner: () => {
      const bannerSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="100" viewBox="0 0 200 100" preserveAspectRatio="none">
        <rect x="1" y="1" width="198" height="98" fill="rgba(255, 255, 255, 0.9)" stroke="black" stroke-width="2" stroke-dasharray="6, 3" rx="4" ry="4"/>
      </svg>`;
      const bannerSrc = "data:image/svg+xml;base64," + btoa(bannerSvg);
      const bannerData = {
        src: bannerSrc,
        fileName: "banner.png",
        isBanner: true,
        width: 200,
        height: 100
      };
      addImage(bannerData, false).then(img => {
        if (img) {
          snapBannerToGrid(img);
        }
      });
    },
    addText: (curentTextStyle) => {
      addText(curentTextStyle, true, true)
    },
    updateText: (curentTextStyle) => {
      if (canvas) {
        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject.type === 'i-text') {
          modifyObject(activeObject, curentTextStyle, true);
          activeObject.dirty = true;
        }
      }
    },
    bringForward: () => {
      if (canvas) {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
          canvas.bringForward(activeObject);
        }
        updateCanvasState(getAllImportantInformations(activeObject), true);
      }
    },
    bringBackward: () => {
      if (canvas) {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
          const canvasObjects = canvas.getObjects();
          const lastRectIndex = canvasObjects.findLastIndex(obj => obj.type === 'rect' && obj !== activeObject);
          const activeObjectIndex = canvas.getObjects().indexOf(activeObject);

          if (activeObjectIndex > lastRectIndex + 1) {
            canvas.sendBackwards(activeObject);
            updateCanvasState(getAllImportantInformations(activeObject), true);

          }
          // canvas.sendBackwards(activeObject);
        }
      }
    },
    removeObject: (history = true) => {
      const activeObject = canvas.getActiveObject();
      if (canvas && activeObject) {
        if (activeObject.pairId) {
          const ghost = canvas.getObjects().find(o => o.id === activeObject.pairId);
          if (ghost) {
            removeObject(ghost, false);
          }
        }
        removeObject(activeObject, history);
      }
    },
    removeLogo: () => {
      if (canvas) {
        removeObject(iParaventLogo);
      }
    },
    cloneObject: () => {
      if (canvas) {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
          const params = getAllImportantInformations(activeObject);
          params.left = activeObject.left + 20;
          params.id = uuidv4();
          params.id = uuidv4();
          const isImage = activeObject.type !== 'i-text' && activeObject.type !== 'text';
          if (activeObject && (isImage || activeObject.type === 'i-text')) {
            if (isImage) {
              addImage(params, false);
            }
            else {
              addText(params, true, true);
            }
          }
        }

      }
    },
    multiplyObject: async (count) => {
      if (!canvas || count < 2) return;
      const activeObject = canvas.getActiveObject();
      if (!activeObject) return;

      const canvasWidth = parseFloat(svgDimensions.width);
      const spacing = canvasWidth / count;
      const batchChanges = [];

      for (let i = 1; i < count; i++) {
        const params = getAllImportantInformations(activeObject);
        params.left = (activeObject.left + i * spacing) % canvasWidth;
        params.id = uuidv4();

        let newObj;
        const isImage = activeObject.type !== 'i-text' && activeObject.type !== 'text';
        if (isImage) {
          // addImage/addText already update iImageGroup/iTextGroup and add to canvas
          newObj = await addImage(params, false, false, false);
        } else {
          newObj = await addText(params, false, false);
        }

        if (newObj) {
          batchChanges.push(getAllImportantInformations(newObj));
          // Handle ghosting for the newly created clone
          const ghostResult = await createGhost(newObj, true);
          if (ghostResult.added) batchChanges.push(ghostResult.added);
          if (ghostResult.removed) batchChanges.push(ghostResult.removed);
        }
      }

      if (batchChanges.length > 0) {
        updateCanvasState(batchChanges, true);
      }
      canvas.renderAll();
    },
    undo: () => {
      if (history.length > 0) {
        const lastChanges = history.at(-1);
        applyChangesToCanvas(lastChanges, "undo")
        setRedoStack((prevRedoStack) => [...prevRedoStack, lastChanges]);
        setHistory((prevHistory) => {
          return prevHistory.slice(0, -1);
        });
      }
    },
    redo: () => {
      if (redoStack.length > 0) {
        const lastChanges = redoStack.at(-1);
        applyChangesToCanvas(lastChanges, "redo")
        setRedoStack((prevRedoStack) => [...prevRedoStack.slice(0, -1)])
        setHistory((prevHistory) => {
          const newHistory = [...prevHistory, lastChanges];
          return newHistory.slice(newHistory.length - historyLimit);
        });
      }
    },
    getShareModalEnvelope2D: async () => {
      if (canvas) {
        const originalTool = selectedTool;

        try {
          setSelectedTool("Graphic");
          await new Promise(resolve => setTimeout(resolve, 0));

          const usedFonts = new Set();
          canvas.getObjects().forEach(obj => {
            if (obj.type === 'i-text' || obj.type === 'text') {
              if (obj.fontFamily) usedFonts.add(obj.fontFamily);
            }
          });

          let fontCss = '';
          if (usedFonts.size > 0) {
            const fontConfig = {
              'BalsamicSans': { googleName: 'Balsamiq+Sans', alias: 'Balsamiq Sans' },
              'ChakraPetch': { googleName: 'Chakra+Petch', alias: 'Chakra Petch' },
              'CrimsonText': { googleName: 'Crimson+Text', alias: 'Crimson Text' },
              'Mali': { googleName: 'Mali' },
              'Merriweather': { googleName: 'Merriweather' },
              'Montserrat': { googleName: 'Montserrat' },
              'Roboto': { googleName: 'Roboto' },
              'ZillaSlab': { googleName: 'Zilla+Slab', alias: 'Zilla Slab' }
            };

            const families = [];
            const aliases = [];

            usedFonts.forEach(font => {
              const config = fontConfig[font];
              if (config) {
                families.push(`family=${config.googleName}:ital,wght@0,400;0,700;1,400;1,700`);
                if (config.alias) {
                  aliases.push(`@font-face { font-family: '${font}'; src: local('${config.alias}'); }`);
                }
              }
            });

            if (families.length > 0) {
              const googleFontsUrl = `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
              fontCss = `
                @import url('${googleFontsUrl}');
                ${aliases.join('\n')}
              `;
            }
          }

          const svg = canvas.toSVG();
          const styledSvg = fontCss
            ? svg.replace(/(<svg[^>]*>)/, `$1<style type="text/css"><![CDATA[${fontCss}]]></style>`)
            : svg;

          const base64SVG = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(styledSvg)));
          return base64SVG;
        } finally {
          setSelectedTool(originalTool);
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }
      return null;
    },
    getEnvelope2D: async () => {
      if (canvas && iEnvelopeGrid) {
        const originalTool = selectedTool;
        const tempObjects = [];
        const horizontalLines = [];
        const verticalLines = [];
        const hiddenStrokes = [];
        const originalVisibility = new Map();
        const originalClipPaths = new Map();

        try {
          setSelectedTool("Graphic");
          await new Promise(resolve => setTimeout(resolve, 0));
          canvas.renderAll();
          iEnvelopeGrid.flat().forEach(group => {
            if (group) {
              group.getObjects().forEach(obj => {
                if (obj.stroke && obj.stroke !== 'transparent') {
                  hiddenStrokes.push({
                    obj: obj,
                    stroke: obj.stroke, strokeWidth: obj.strokeWidth, strokeUniform: obj.strokeUniform
                  });
                  obj.set({ stroke: 'transparent', strokeWidth: 0 });
                }
              });
            }
          });

          let minTop = Infinity, maxBottom = -Infinity, minLeft = Infinity, maxRight = -Infinity;
          iEnvelopeGrid.flat().forEach(group => {
            if (group) {
              minTop = Math.min(minTop, group.top);
              maxBottom = Math.max(maxBottom, group.top + group.height);
              minLeft = Math.min(minLeft, group.left);
              maxRight = Math.max(maxRight, group.left + group.width);
            }
          });

          const absoluteTop = minTop === Infinity ? 0 : minTop;
          const absoluteBottom = maxBottom === -Infinity ? 0 : maxBottom;
          const absoluteLeft = minLeft === Infinity ? 0 : minLeft;
          const absoluteRight = maxRight === -Infinity ? 0 : maxRight;

          const drawnX = [], drawnY = [];
          const shouldDraw = (val, list) => {
            if (list.some(existing => Math.abs(existing - val) <= 5)) return false;
            list.push(val);
            return true;
          };

          const addBorderedGridRect = (coord, color, isVertical) => {
            const roundedCoord = Math.round(coord);
            const rectParams = { selectable: false, evented: false, objectCaching: false, id: 'grid-line' };

            const blackBase = new fabric.Rect({
              ...rectParams,
              fill: '#000000',
              opacity: 0.5,
              left: isVertical ? roundedCoord - 1 : absoluteLeft,
              top: isVertical ? absoluteTop : roundedCoord - 1,
              width: isVertical ? 3 : absoluteRight - absoluteLeft,
              height: isVertical ? absoluteBottom - absoluteTop : 3
            });

            const centerLine = new fabric.Rect({
              ...rectParams,
              fill: color,
              opacity: 1,
              left: isVertical ? roundedCoord : absoluteLeft,
              top: isVertical ? absoluteTop : roundedCoord,
              width: isVertical ? 1 : absoluteRight - absoluteLeft,
              height: isVertical ? absoluteBottom - absoluteTop : 1
            });

            canvas.add(blackBase, centerLine);
            tempObjects.push(blackBase, centerLine);
            if (isVertical) {
              verticalLines.push(blackBase, centerLine);
            } else {
              horizontalLines.push(blackBase, centerLine);
            }
          };

          iEnvelopeGrid.forEach((row, r) => {
            if (!row) return;
            const isParavent = r >= (iEnvelopeGrid.length - paraventSize);
            const horizColor = isParavent ? '#FFFFFF' : '#F5D671';

            row.forEach((group, c) => {
              if (!group) return;

              if (shouldDraw(group.left, drawnX)) {
                addBorderedGridRect(group.left, '#5DA9E9', true);
              }
              if (c === row.length - 1 && shouldDraw(group.left + group.width, drawnX)) {
                addBorderedGridRect(group.left + group.width, '#5DA9E9', true);
              }

              if (shouldDraw(group.top, drawnY)) {
                addBorderedGridRect(group.top, horizColor, false);
              }
              if (shouldDraw(group.top + group.height, drawnY)) {
                addBorderedGridRect(group.top + group.height, horizColor, false);
              }
            });
          });

          tempObjects.forEach(obj => obj.bringToFront());

          canvas.getObjects().forEach(obj => {
            if (obj.id === 'paravent-grid' || obj === iParaventLogo) {
              return;
            }

            if (obj.clipPath && obj.clipPath.id === 'paravent-grid') {
              originalClipPaths.set(obj, obj.clipPath);
              obj.set('clipPath', null);
            }

            originalVisibility.set(obj, obj.visible);
            obj.set('visible', true);
          });

          canvas.renderAll();

          const base64Image = canvas.toDataURL({
            format: 'jpeg',
            quality: 1,
            multiplier: 2
          });
          return base64Image;

        } finally {
          originalVisibility.forEach((visible, obj) => {
            obj.set('visible', visible);
          });
          originalClipPaths.forEach((clipPath, obj) => {
            obj.set('clipPath', clipPath);
          });
          hiddenStrokes.forEach(item => {
            item.obj.set({ stroke: item.stroke, strokeWidth: item.strokeWidth, strokeUniform: item.strokeUniform });
          });
          tempObjects.forEach(obj => canvas.remove(obj));
          setSelectedTool(originalTool);
          canvas.renderAll();
        }
      }
      return null;
    },
    setNomexRows: (count) => {
      if (!canvas || !iEnvelopeGrid || count === canvasState?.nomexBottomRows) return;

      const currentRows = canvasState?.nomexBottomRows || 1;
      const changes = [];

      // Update nomexBottomRows state change
      changes.push({ path: 'nomexBottomRows', old: currentRows, new: count });

      const loopMax = Math.max(currentRows, count);

      for (let r = 0; r < loopMax; r++) {
        if (r < iEnvelopeGrid.length) { // Safety check
          const isNomex = r < count;
          const targetColor = isNomex ? (selectedNomexColor?.hex || defaultNomexColor?.hex) : (defaultColor?.hex || '#ffffff');

          iEnvelopeGrid[r].forEach((targetGroup) => {
            if (targetGroup) {
              const { row, col } = getCoordinatesFromId(targetGroup.id);
              if (canvasState.envelopeGrid[row][col] !== targetColor) {
                const targetObject = targetGroup.getObjects()[0];
                changes.push({ path: 'envelopeGrid', row: row, col: col, old: targetObject.fill, new: targetColor });
                targetObject.set('fill', targetColor);
                targetObject.dirty = true;
              }

              // Update stroke based on whether it is a Nomex row
              targetGroup.getObjects().forEach(object => {
                if (isNomex || r === iEnvelopeGrid.length - 1) { // Also hide for top row (maxRow)
                  object.set('stroke', null);
                  object.set('strokeWidth', 0);
                  object.set('strokeUniform', true);
                  object.dirty = true;
                } else {
                  // Restore stroke if reverting from Nomex
                  if (object.stroke === null || object.stroke === 'transparent') {
                    object.set('stroke', 'grey');
                    object.set('strokeWidth', 0.5);
                    object.set('strokeUniform', true);
                    object.dirty = true;
                  }
                }
              });
            }
          });
        }
      }

      // Update clip path
      const topY = iEnvelopeGrid.at(-paraventSize - 1)[0]?.top || 0;
      const bottomY = iEnvelopeGrid[count - 1]?.[0]?.top || 0;

      if (envelopeClipPath) {
        envelopeClipPath.set({
          height: bottomY - topY,
          top: topY
        });
        envelopeClipPath.dirty = true;
        setEnvelopeDimension(prev => ({
          ...prev,
          height: bottomY - topY,
          top: topY
        }));
      }

      setHistory((prevHistory) => {
        const newHistory = [...prevHistory, changes];
        return newHistory.slice(newHistory.length - historyLimit);
      });
      setRedoStack([]);

      setCanvasState((prevCanvasState) => {
        const nextState = { ...prevCanvasState };
        nextState.nomexBottomRows = count;

        changes.forEach((change) => {
          if (change.path === 'envelopeGrid') {
            nextState.envelopeGrid[change.row][change.col] = change.new;
          }
        });

        canvasStateRef.current = nextState;
        return nextState;
      });

      canvas.requestRenderAll();
    }
  }), [canvas, iEnvelopeGrid, iTextGroup, history, redoStack, selectedTool, svgDimensions.width, canvasState, selectedNomexColor, defaultColor, defaultNomexColor, paraventSize, envelopeClipPath]);



  const getCoordinatesFromId = (id) => {
    const parts = id.split('-');
    if (parts.length === 2) {
      return { row: parseInt(parts[0]), col: parseInt(parts[1]) - 1 };
    }
    return null;
  }

  const createGradient = (rectWidth, rectHeight) => {
    return new fabric.Gradient({
      type: 'linear',
      coords: {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: rectHeight
      },
      colorStops: [
        { offset: 0, color: 'white' },
        { offset: 1, color: 'black' }
      ]
    });
  }

  const selectAllRectangles = () => {
    if (canvas) {
      return iEnvelopeGrid.slice(1).flat();
    } else {
      return [];
    }
  };

  const selectEntireRow = (rowNumber) => {
    if (canvas && rowNumber >= 1) {
      return iEnvelopeGrid[rowNumber];
    } else {
      return [];
    }
  };

  const selectEntireColumn = (columnNumber) => {
    if (canvas) {
      return iEnvelopeGrid.slice(1, -1).map((row) => row[columnNumber]);
    } else {
      return [];
    }
  };

  const selectDiagonalLine = (clickedRow, clickedCol, direction) => {
    if (canvas) {
      const rows = iEnvelopeGrid.length;
      const cols = iEnvelopeGrid[0].length;
      const selectedElements = [];

      for (let row = 1; row < rows - 1; row++) {
        for (let col = 0; col < cols; col++) {
          if (direction === 'up') {
            if (Math.abs((row - col) - (clickedRow - clickedCol)) % cols === 0) {
              selectedElements.push(iEnvelopeGrid[row][col]);
            }
          } else if (direction === 'down') {
            if (Math.abs((row + col) - (clickedRow + clickedCol)) % cols === 0) {
              selectedElements.push(iEnvelopeGrid[row][col]);
            }
          }
        }
      }

      return selectedElements;
    } else {
      return [];
    }
  };

  const selectSameColor = (target) => {
    if (canvas) {
      const allRects = selectAllRectangles();
      const selectedRects = [];
      const targetColor = target.getObjects()[0].fill;
      allRects.forEach(rect => {
        if (rect.getObjects()[0].fill === targetColor) {
          selectedRects.push(rect);
        }
      });
      return selectedRects;
    } else {
      return [];
    }
  }

  const getTargetRectangles = (target) => {

    const { row, col } = getCoordinatesFromId(target.id);
    const nomexRows = canvasState?.nomexBottomRows || 1;
    let candidates = [];

    switch (selectedTool) {
      case "Panel":
        if (row === iEnvelopeGrid.length - 1) {
          candidates = selectEntireRow(row);
        } else {
          candidates = [target];
        }
        break;
      case "Row":
        candidates = selectEntireRow(row);
        break;
      case "Gore":
        if (row === iEnvelopeGrid.length - 1) {
          candidates = [];
        } else {
          candidates = selectEntireColumn(col);
        }
        break;
      case "Envelope":
        candidates = selectAllRectangles();
        break;
      case "SpiralUp":
        if (row === iEnvelopeGrid.length - 1) {
          candidates = [];
        } else {
          candidates = selectDiagonalLine(row, col, "up");
        }
        break;
      case "Spiral":
        if (row === iEnvelopeGrid.length - 1) {
          candidates = [];
        } else {
          candidates = selectDiagonalLine(row, col, "down");
        }
        break;
      case "Swap":
        candidates = selectSameColor(target);
        break;
      default:
        candidates = [];
    }

    // Filter out Nomex rows
    if (candidates.length > 0) {
      return candidates.filter(candidate => {
        const coords = getCoordinatesFromId(candidate.id);
        return coords && coords.row >= nomexRows;
      });
    }
    return candidates;

  }

  const clearCanvas = () => {
    if (canvas) {
      canvas.getObjects().forEach(object => {
        canvas.remove(object);
      });
      canvas.requestRenderAll();

      setIEnvelopeGrid([]);
      setIParaventGrid(null);
      setIImageGroup([]);
      setIParaventLogo(null);
      setITextGroup([]);
      canvas.off('object:modified')
      canvas.off('mouse:out')

    }
  };

  const handleResize = () => {
    fitResponsiveCanvas(canvasRef.current, svgDimensions);
  };

  useEffect(() => {
    const canvas = new fabric.Canvas(canvasContainerRef.current, {
      width: svgDimensions.width, height: svgDimensions.height,
      backgroundColor: "#303030",
    });

    setCanvas(canvas)
    canvasRef.current = canvas;

    const handleWindowResize = () => {
      setTimeout(() => {
        handleResize();
        if (canvasRef.current) {
          setShowArrows(window.innerWidth > canvasRef.current.width + 80);
          canvasRef.current.renderAll();
        }
      }, 500);
    };

    window.addEventListener('resize', handleWindowResize, { passive: true });
    document.addEventListener('fullscreenchange', handleWindowResize);
    fitResponsiveCanvas(canvasRef.current, svgDimensions)
    setShowArrows(window.innerWidth > canvasRef.current.width + 80);
    canvas.requestRenderAll();

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      document.removeEventListener('fullscreenchange', handleWindowResize);
      canvas.dispose();
    }
  }, []);

  useEffect(() => {
    if (canvas) {
      const timeoutId = setTimeout(() => {
        handleResize();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [viewMode]) // Re-run resize logic when switching modes, but don't add new listeners

  const fitResponsiveCanvas = (canvas, svgDimensions) => {
    const vMode = viewModeRef.current;
    const isFullscreen = !!document.fullscreenElement;
    const viewportWidth = (vMode === "Grid" && isFullscreen) ? (window.screen.width || window.innerWidth) : window.innerWidth;

    const aspectRatio = svgDimensions.width / svgDimensions.height;
    let newHeight = viewportWidth / aspectRatio;
    let newWidth = newHeight * aspectRatio;
    if (Math.floor(newWidth) > viewportWidth) {
      newWidth = viewportWidth;
      newHeight = newWidth / aspectRatio;
    }
    const maxGridHeight = isFullscreen ? window.innerHeight : window.innerHeight - 135;
    const gridViewConstraint = isFullscreen ? Infinity : maxGridHeight;
    if (Math.floor(newHeight) > (vMode === "Grid" ? gridViewConstraint : window.innerHeight * 0.35)) {
      newHeight = vMode === "Grid" ? gridViewConstraint : window.innerHeight * 0.35;
      newWidth = newHeight * aspectRatio;
      if (Math.floor(newWidth) > window.innerWidth && vMode !== "Grid") {
        newWidth = window.innerWidth;
        newHeight = newWidth / aspectRatio;
      }
    }
    if (vMode === "Grid" && isFullscreen) {
      newWidth = viewportWidth;
      newHeight = newWidth / aspectRatio;
    }
    if (canvas.width !== newWidth || canvas.height !== newHeight) {
      let userZoomRatio = 1;
      if (canvas.width > 0 && canvas.getZoom() > 0) {
        userZoomRatio = canvas.getZoom() / (canvas.width / svgDimensions.width);
      }
      canvas.setDimensions({ width: newWidth, height: newHeight });
      canvas.setZoom((newWidth / svgDimensions.width) * userZoomRatio);
    }
  }

  useEffect(() => {
    if (canvas && iEnvelopeGrid) {
      let lastHoveredGroup = null;
      selectAllRectangles().forEach((rectGroup) => {
        rectGroup.off('mousedown')
        rectGroup.off('mouseover')
        rectGroup.off('mouseout')
        rectGroup.on('mousedown', () => {
          const changes = [];
          getTargetRectangles(rectGroup).forEach((targetGroup) => {
            const { row, col } = getCoordinatesFromId(targetGroup.id);
            if (canvasState.envelopeGrid[row][col] !== selectedColor.hex) {
              const targetObject = targetGroup.getObjects()[0]
              changes.push({ path: 'envelopeGrid', row: row, col: col, old: targetObject.fill, new: selectedColor.hex })
              targetGroup.getObjects()[0].set('fill', selectedColor.hex);
              targetGroup.getObjects()[0].dirty = true
            }
          });
          setHistory((prevHistory) => {
            const newHistory = [...prevHistory, changes];
            return newHistory.slice(newHistory.length - historyLimit);
          });
          setRedoStack([]);
          setCanvasState((prevCanvasState) => {
            changes.forEach((change) => {
              prevCanvasState.envelopeGrid[change.row][change.col] = change.new;
            })
            return {
              ...prevCanvasState
            }
          })
          canvas.requestRenderAll()
          setHighLightColor(rectGroup.getObjects()[0].fill);
        })
        rectGroup.on('mouseover', (e) => {
          const targetRectangles = getTargetRectangles(rectGroup)
          targetRectangles.forEach((targetGroup) => {
            targetGroup.getObjects()[1].set('visible', true);
            targetGroup.getObjects()[1].dirty = true
          });
          canvas.requestRenderAll()
          lastHoveredGroup = targetRectangles;
          setHighLightColor(rectGroup.getObjects()[0].fill);
        })
        rectGroup.on('mouseout', () => {
          const targetRectangles = getTargetRectangles(rectGroup)
          targetRectangles.forEach((targetGroup) => {
            targetGroup.getObjects()[1].set('visible', false);
            targetGroup.getObjects()[1].dirty = true
          });
          canvas.requestRenderAll()
          setHighLightColor(null);
        })
      })
      canvas.on('mouse:out', () => {
        if (lastHoveredGroup) {
          lastHoveredGroup.forEach((targetGroup) => {
            targetGroup.getObjects()[1].set('visible', false);
            targetGroup.getObjects()[1].dirty = true
          });
          canvas.requestRenderAll()
          setHighLightColor(null);
        }
      });
    }

  }, [selectedTool, selectedColor, iEnvelopeGrid]);

  useEffect(() => {
    if (canvas && iEnvelopeGrid && iParaventGrid && iImageGroup && iTextGroup) {
      iEnvelopeGrid.flat().forEach((rectGroup) => {
        rectGroup.set('evented', ["Panel", "Row", "Gore", "SpiralUp", "Spiral", "Swap", "Envelope"].includes(selectedTool) ? true : false)
        rectGroup.set('visible', "Paravent" !== selectedTool)
      });
      iParaventGrid.set('visible', selectedTool === "Paravent")
      iParaventLogo?.set('visible', selectedTool === "Paravent")
      iParaventLogo?.set('evented', selectedTool === "Paravent")
      iLabel?.set('visible', selectedTool !== "Paravent")
      iImageGroup.forEach((image) => {
        const isGraphicOrText = ["Graphic", "Text"].includes(selectedTool);
        const shouldBeSelectable = selectedTool === 'Graphic' && !image.isGhost;
        image.set({
          selectable: shouldBeSelectable,
          'evented': shouldBeSelectable,
          visible: isGraphicOrText,
          dirty: true
        })
      });
      iTextGroup.forEach((text) => {
        const isGraphicOrText = ["Graphic", "Text"].includes(selectedTool);
        const shouldBeSelectable = selectedTool === 'Text' && !text.isGhost;
        text.set({
          selectable: shouldBeSelectable,
          'evented': shouldBeSelectable,
          visible: isGraphicOrText,
          dirty: true
        })
        text.bringToFront();
      });

      canvas.discardActiveObject()
      if (typeof enforceLayering === 'function') enforceLayering();
      canvas.renderAll();
    }
  }, [selectedTool, canvas, iEnvelopeGrid, iParaventGrid, iImageGroup, iTextGroup, iParaventLogo, iLabel])

  useEffect(() => {
    if (canvasState && canvas && selectedTool === "Nomex") {
      const changes = [];

      const rowsToColor = canvasState.nomexBottomRows || 1;

      iEnvelopeGrid.slice(0, rowsToColor).forEach((row) => {
        row.forEach((targetGroup) => {
          if (targetGroup) {
            const { row, col } = getCoordinatesFromId(targetGroup.id);
            if (canvasState.envelopeGrid[row][col] !== selectedNomexColor.hex) {
              const targetObject = targetGroup.getObjects()[0]
              changes.push({ path: 'envelopeGrid', row: row, col: col, old: targetObject.fill, new: selectedNomexColor.hex })
              targetGroup.getObjects()[0].set('fill', selectedNomexColor.hex);
              targetGroup.getObjects()[0].dirty = true
            }
          }
        });
      });
      setHistory((prevHistory) => {
        const newHistory = [...prevHistory, changes];
        return newHistory.slice(newHistory.length - historyLimit);
      });
      setRedoStack([]);
      setCanvasState((prevCanvasState) => {
        changes.forEach((change) => {
          prevCanvasState.envelopeGrid[change.row][change.col] = change.new;
        })
        return {
          ...prevCanvasState
        }
      })
      canvas.requestRenderAll()
    }
  }, [selectedNomexColor])

  useEffect(() => {
    if (canvasState?.nomexBottomRows && iEnvelopeGrid && envelopeClipPath) {
      const topY = iEnvelopeGrid.at(-paraventSize - 1)[0]?.top || 0;
      const bottomY = iEnvelopeGrid[canvasState.nomexBottomRows - 1]?.[0]?.top || 0;

      envelopeClipPath.set({
        height: bottomY - topY,
        top: topY
      });
      envelopeClipPath.dirty = true;
      setEnvelopeDimension(prev => ({
        ...prev,
        height: bottomY - topY,
        top: topY
      }));
      canvas.requestRenderAll();
    }
  }, [canvasState?.nomexBottomRows]);


  useEffect(() => {
    if (canvas && svgContent && defaultColor && defaultNomexColor) {
      clearCanvas();

      let maxRow = 0;
      let maxCol = 0;

      const tEnvelopePanels = [];
      const tCanvasState = canvasState ? canvasState : {
        envelopeGrid: [],
        images: [],
        texts: [],
        paraventLogo: null,
        label: null,
        nomexBottomRows: 1,
        loadlines: '#FFFFFF',
      };
      const panelsIDs = [];

      svgContent.children.forEach((child) => {
        panelsIDs.push(child.properties.id);
        const properties = child.properties;
        const coords = getCoordinatesFromId(properties.id);
        if (coords) {
          maxRow = Math.max(maxRow, coords.row);
          maxCol = Math.max(maxCol, coords.col);
        }

        const panel = new fabric.Rect({
          id: properties.id,
          width: properties.width,
          height: properties.height,
          fill: coords.row === 0 ? defaultNomexColor.hex : defaultColor.hex,
          strokeUniform: true,
          stroke: 'grey',
          strokeWidth: 0.5,
          evented: false
        });

        const overlay = new fabric.Rect({
          id: "gradient-" + properties.id,
          width: panel.width,
          height: panel.height,
          fill: createGradient(panel.width, panel.height),
          opacity: 0.2,
          evented: false,
          visible: false,
          objectCaching: true
        });

        const panelGroup = new fabric.Group([panel, overlay], {
          id: properties.id,
          left: properties.x || 0,
          top: properties.y || 0,
          evented: true,
          selectable: false,
          hoverCursor: "pointer",
        });
        tEnvelopePanels.push(panelGroup);
      });

      canvas.renderOnAddRemove = false;

      const tParaventGrid = new fabric.Circle({
        id: "paravent-grid",
        radius: svgDimensions.height / 2,
        fill: 'grey',
        selectable: false,
        evented: false,
        visible: false,
        left: svgDimensions.width / 2 - svgDimensions.height / 2,
        absolutePositioned: true,
        top: 0,
      });
      canvas.add(tParaventGrid);

      const tEnvelopeGrid = Array.from({ length: maxRow + 1 }, () => Array(maxCol + 1).fill(null));
      if (!canvasState || !canvasState.envelopeGrid) {
        tCanvasState.envelopeGrid = Array.from({ length: maxRow + 1 }, () => Array(maxCol + 1).fill(null));
      }
      tEnvelopePanels.forEach((panelGroup) => {
        const coords = getCoordinatesFromId(panelGroup.id);
        if (coords) {
          canvas.add(panelGroup);
          let fill = null;
          try {
            fill = tCanvasState.envelopeGrid[coords.row][coords.col];
          } catch (e) {
            console.log(e);
          }
          if (fill) {
            panelGroup.getObjects()[0].set('fill', fill);
          } else {
            const isNomexRow = coords.row < (tCanvasState.nomexBottomRows || 1);
            panelGroup.getObjects()[0].set('fill', isNomexRow ? defaultNomexColor.hex : defaultColor.hex);
            try {
              tCanvasState.envelopeGrid[coords.row][coords.col] = panelGroup.getObjects()[0].fill;
            } catch (e) {
              console.log(e);
            }
          }
          tEnvelopeGrid[coords.row][coords.col] = panelGroup;
          if (coords.row < (tCanvasState.nomexBottomRows || 1) || coords.row === maxRow) {
            panelGroup.getObjects().forEach(object => {
              object.set('stroke', null);
              object.set('strokeWidth', 0);
              object.set('strokeUniform', true);
            });
          }
        }
      });

      const clipPath = new fabric.Rect({
        left: 0,

        top: tEnvelopeGrid.at(-paraventSize - 1)[0]?.top || 0,
        height: (tEnvelopeGrid[(tCanvasState.nomexBottomRows || 1) - 1]?.[0]?.top || 0) - (tEnvelopeGrid.at(-paraventSize - 1)[0]?.top || 0),
        width: parseFloat(svgDimensions.width),
        absolutePositioned: true,
      });

      setEnvelopeDimension({
        width: clipPath.width,
        height: clipPath.height,
        top: clipPath.top,
        left: clipPath.left,
        paraventRadius: tParaventGrid.radius
      });

      setEnvelopeClipPath(clipPath);
      setIEnvelopeGrid(tEnvelopeGrid);
      setIParaventGrid(tParaventGrid);

      const boundaryY = tEnvelopeGrid.at(-paraventSize - 1)?.[0]?.top || 0;
      setArrowY(boundaryY);

      const loadImageAsync = (url) => {
        return new Promise((resolve, reject) => {
          if (url && (url.startsWith('data:image/svg+xml') || url.endsWith('.svg'))) {
            fabric.loadSVGFromURL(url, (objects, options) => {
              if (objects) {
                const grouped = fabric.util.groupSVGElements(objects, options);
                resolve(grouped);
              } else {
                reject(new Error(`Failed to load SVG: ${url}`));
              }
            });
            return;
          }
          fabric.Image.fromURL(url, (img) => {
            if (img) {
              resolve(img);
            } else {
              reject(new Error(`Failed to load image: ${url}`));
            }
          });
        });
      };

      // Use an async IIFE to handle initialization and set the state only once.
      (async () => {
        const imageLoadPromises = (canvasState?.images || []).map(image => loadImageAsync(image.src).then(img => ({ img, data: image })).catch(e => { console.error(e); return null; }));

        const loadedImages = await Promise.all(imageLoadPromises);

        loadedImages.forEach(result => {
          if (result) {
            addImage(result.data, false, false, false, clipPath);
          }
        });

        if (canvasState) {
          canvasState.texts?.forEach((text) => {
            addText(text, false, false);
          });
          if (canvasState.paraventLogo) {
            addImage(canvasState.paraventLogo, true, false, false, tParaventGrid);
          }
        }

        // This block handles re-drawing the label from an existing state.
        if (canvasState && canvasState.label && canvasState.label.src) {
          try {
            const img = await loadImageAsync(canvasState.label.src);
            img.set({
              left: canvasState.label.left,
              top: canvasState.label.top,
              scaleX: canvasState.label.scaleX,
              scaleY: canvasState.label.scaleY,
              id: canvasState.label.id,
              selectable: false,
              evented: false,
            });
            canvas.add(img);
            img.bringToFront();
            setILabel(img);
          } catch (error) {
            console.error("Error reloading label from state:", error);
          }
        }
        // This block handles creating the label when it's missing (new canvas, or a loaded state without one).
        else {
          const targetPanelForLabel = tEnvelopeGrid[0]?.[0];
          if (targetPanelForLabel && targetPanelForLabel.id === '00-1') {
            try {
              const base64Label = await toBase64(icons.labelPng);
              const img = await loadImageAsync(base64Label);

              const panelHeight = targetPanelForLabel.getScaledHeight();
              const panelWidth = targetPanelForLabel.getScaledWidth();
              const newLabelHeight = panelHeight * (2 / 7);
              img.scaleToHeight(newLabelHeight);

              const newLabelWidth = img.getScaledWidth();
              const labelLeft = targetPanelForLabel.left + (panelWidth / 2) - (newLabelWidth / 2);
              const labelTop = targetPanelForLabel.top + (panelHeight / 2) - (newLabelHeight / 2);

              img.set({
                left: labelLeft,
                top: labelTop,
                id: 'LABEL',
                selectable: false,
                evented: false,
              });

              canvas.add(img);
              img.bringToFront();
              setILabel(img);

              // Add the label data to our temporary state object before the final update.
              tCanvasState.label = getAllImportantInformations(img);

            } catch (error) {
              console.error("Error initializing label:", error);
            }
          }
        }

        // This is now the single point where the state is committed.
        setCanvasState(tCanvasState);
        canvas.renderOnAddRemove = true;
        canvas.requestRenderAll();
      })();

      canvas.on('mouse:down', (e) => {
        if (e.target && e.target.pairId) {
          permanentGhostRef.current = canvas.getObjects().find(o => o.id === e.target.pairId);
        }
      });

      canvas.on('object:rotating', handleTransform);
      canvas.on('object:scaling', handleTransform);
      canvas.on('object:skewing', handleTransform);
      canvas.on('object:modified', handleTransform);

      canvas.on('selection:created', enforceLayering);
      canvas.on('selection:updated', enforceLayering);
      canvas.on('selection:cleared', enforceLayering);

      canvas.on('object:moving', (e) => {
        const movingObject = e.target;
        const canvasWidth = parseFloat(svgDimensions.width);
        const objWidth = movingObject.getScaledWidth();

        handleTransform(e);

        const isCrossing = movingObject.left < 0 || movingObject.left + objWidth > canvasWidth;

        if (isCrossing && !temporaryGhostRef.current && !isCreatingTempGhostRef.current) {
          isCreatingTempGhostRef.current = true;
          movingObject.clone((cloned) => {
            temporaryGhostRef.current = cloned;
            temporaryGhostRef.current.set({
              evented: false,
              selectable: false,
              clipPath: movingObject.clipPath
            });
            canvas.add(temporaryGhostRef.current);
            isCreatingTempGhostRef.current = false;
          });
        } else if (!isCrossing && temporaryGhostRef.current) {
          canvas.remove(temporaryGhostRef.current);
          temporaryGhostRef.current = null;
        }

        if (temporaryGhostRef.current) {
          syncGhost(movingObject, temporaryGhostRef.current);
        }
      });

      canvas.on('object:modified', (e) => {
        const modifiedObject = e.target;

        if (modifiedObject && modifiedObject.isBanner) {
          snapBannerToGrid(modifiedObject);
        }

        if (temporaryGhostRef.current) {
          canvas.remove(temporaryGhostRef.current);
          temporaryGhostRef.current = null;
        }

        permanentGhostRef.current = null;

        const wrappedX = getWrappedPosition(modifiedObject.left, svgDimensions.width);
        if (Math.abs(modifiedObject.left - wrappedX) > 0.1) {
          modifiedObject.set('left', wrappedX);
          modifiedObject.setCoords();
          canvas.setActiveObject(modifiedObject);
          canvas.requestRenderAll();
        }

        const masterInfo = getAllImportantInformations(modifiedObject);
        createGhost(modifiedObject, true).then(ghostResult => {
          const batch = [masterInfo];
          if (ghostResult.added) batch.push(ghostResult.added);
          if (ghostResult.removed) batch.push(ghostResult.removed);
          updateCanvasState(batch, true);
        });
      });

      canvas.on('font:loaded', () => {
        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject.type === 'i-text') {
          activeObject.dirty = true;
        }
        canvas.requestRenderAll();
      });

      canvas.on('object:scaling', (e) => {
        var scaledObject = e.target;
        if (scaledObject.flipX === true || scaledObject.flipY === true) {
          scaledObject.flipX = false;
          scaledObject.flipY = false;
        }
      });

      return () => {
        canvas.off('mouse:down');
        canvas.off('object:moving');
        canvas.off('object:rotating');
        canvas.off('object:scaling');
        canvas.off('object:skewing');
        canvas.off('object:modified');
        canvas.off('selection:created');
        canvas.off('selection:updated');
        canvas.off('selection:cleared');
        canvas.off('font:loaded');
      };
    }
  }, [canvas, svgContent, defaultColor, defaultNomexColor]);

  return (
    <div style={{ ...style, position: 'relative', width: 'fit-content', height: 'fit-content' }}>
      <canvas ref={canvasContainerRef} id="canvas" style={{}} />
      {showArrows && arrowY > 0 && (
        <>
          <div style={{
            position: 'absolute',
            left: '-25px',
            top: `${arrowY * (canvas?.getZoom() || 1)}px`,
            transform: 'translateY(-50%) rotate(180deg)',
            pointerEvents: 'none',
            zIndex: 1,
          }}>
            <black_icons.arrowVent width="24px" height="24px" stroke="#F5D671" />
          </div>
          <div style={{
            position: 'absolute',
            right: '-25px',
            top: `${arrowY * (canvas?.getZoom() || 1)}px`,
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}>
            <black_icons.arrowVent width="24px" height="24px" stroke="#F5D671" />
          </div>
        </>
      )}
    </div>
  );
};

export default React.forwardRef(Canvas);